import * as React from 'react';

import {
  Button,
  Checkbox,
  cn,
  Col,
  FieldGroup,
  Radio,
  RadioGroup,
  Row,
  TextField,
} from '@rematter/ui-web';
import { useAtomValue, useSetAtom } from 'jotai';
import {
  LucideAsterisk,
  LucideCamera,
  LucideGripVertical,
  LucideSlidersHorizontal,
  LucideTrash2,
} from 'lucide-react';

import * as salesPrefState from '../../../_state/locationSalesPreferences.state';
import { FormSection } from '../../_shared/FormSection';

import { SelectPort } from '~/shared/components/select-fields/SelectPort';

type PhotoInstructionRowProps = {
  instruction: salesPrefState.PhotoInstruction;
  index: number;
  isLast: boolean;
  onUpdate: (params: {
    id: string;
    updates: Partial<salesPrefState.PhotoInstruction>;
  }) => void;
  onRemove: (id: string) => void;
  onAdd: (params: { id: string; instruction: string }) => void;
};

const PhotoInstructionRow = ({
  instruction,
  index,
  isLast,
  onUpdate,
  onRemove,
  onAdd,
}: PhotoInstructionRowProps) => {
  const handleTextChange = (value: string) => {
    if (isLast && value) {
      // First keystroke on empty placeholder - create the row with its pre-generated id
      onAdd({ id: instruction.id, instruction: value });
    } else if (!isLast) {
      onUpdate({ id: instruction.id, updates: { instruction: value } });
    }
  };

  const handleRequiredChange = (value: boolean) => {
    if (!isLast) {
      onUpdate({ id: instruction.id, updates: { isRequired: value } });
    }
  };

  return (
    <Row yStretch gap2 className="@PhotoInstructionRow group">
      <Row
        yCenter
        gap1
        className={cn(
          'rounded-md bg-current/10 px-2 py-1.5 opacity-50 group-hover:opacity-100',
          isLast && 'opacity-50',
        )}
      >
        <LucideGripVertical className="size-4 cursor-grab" />
        <span className="w-6 text-sm text-neutral/60">#{index + 1}</span>
      </Row>
      <TextField
        className="w-full flex-1"
        value={instruction.instruction}
        onChange={handleTextChange}
        placeholder="Type photo instruction here..."
      />
      <FieldGroup className={'w-auto'}>
        <Checkbox
          isSelected={instruction.isRequired}
          onChange={handleRequiredChange}
          isDisabled={isLast}
        >
          Required
        </Checkbox>
      </FieldGroup>
      <Button
        icon={<LucideTrash2 />}
        variant="outline"
        color="failure"
        isDisabled={isLast}
        className={isLast ? 'opacity-30' : ''}
        onClick={() => onRemove(instruction.id)}
      />
    </Row>
  );
};

export const SalesModulePreferences = () => {
  const form = useAtomValue(salesPrefState.$locationSalesPreferencesFormState);
  const updateForm = useSetAtom(
    salesPrefState.$updateLocationSalesPreferencesForm,
  );
  const discardChanges = useSetAtom(
    salesPrefState.$discardLocationSalesPreferencesChanges,
  );
  const savePreferences = useSetAtom(
    salesPrefState.$saveLocationSalesPreferences,
  );
  const hasUnsavedChanges = useAtomValue(
    salesPrefState.$hasUnsavedSalesPreferencesChanges,
  );
  const updatePhotoInstruction = useSetAtom(
    salesPrefState.$updatePhotoInstruction,
  );
  const addPhotoInstruction = useSetAtom(salesPrefState.$addPhotoInstruction);
  const removePhotoInstruction = useSetAtom(
    salesPrefState.$removePhotoInstruction,
  );
  const photoInstructionsWithNewRow = useAtomValue(
    salesPrefState.$photoInstructionsWithNewRow,
  );

  const handleDiscard = () => {
    discardChanges();
  };

  const handleSave = () => {
    savePreferences();
  };

  return (
    <Col className="@SalesModulePreferences h-full">
      <Col className="max-h-full flex-1 gap-8 overflow-y-auto p-5">
        <FormSection
          icon={LucideSlidersHorizontal}
          title="Sales Module Defaults"
        >
          <Col gap4>
            <Col gap1>
              <Row className="text-sm font-medium" gap1>
                Default Weight Display Unit
                <LucideAsterisk className="size-3 text-failure" />
              </Row>
              <RadioGroup
                value={form.defaultWeightDisplayUnit}
                onChange={(v) => updateForm({ defaultWeightDisplayUnit: v })}
              >
                <Radio value="SCALE_UNIT">Scale Unit</Radio>
                <Radio value="PRICE_UNIT">Price Unit</Radio>
              </RadioGroup>
            </Col>

            <SelectPort
              label="Default Port"
              isRequired
              value={form.defaultPort || undefined}
              onChange={(value) => updateForm({ defaultPort: value ?? '' })}
              placeholder="Search for a port..."
            />

            <Col gap1>
              <span className="text-sm font-medium">Other</span>
              <Col className="rounded-lg border border-neutral/15">
                <Col className="border-b border-neutral/15 p-3">
                  <Checkbox
                    isSelected={form.enableDefaultPrinting}
                    onChange={(v) => updateForm({ enableDefaultPrinting: v })}
                  >
                    Enable Default Printing
                  </Checkbox>
                  <span className="ml-6 text-sm text-neutral/60">
                    Remembers printer choices for different document types
                  </span>
                </Col>
                <Col className="border-b border-neutral/15 p-3">
                  <Checkbox
                    isSelected={form.skipScalePayment}
                    onChange={(v) => updateForm({ skipScalePayment: v })}
                  >
                    Skip Scale Payment
                  </Checkbox>
                </Col>
                <Col className="p-3">
                  <Checkbox
                    isSelected={form.askForDeletionConfirmation}
                    onChange={(v) =>
                      updateForm({ askForDeletionConfirmation: v })
                    }
                  >
                    Ask for deletion confirmation
                  </Checkbox>
                </Col>
              </Col>
            </Col>
          </Col>
        </FormSection>

        <div className="border-t border-neutral/15" />

        <FormSection icon={LucideCamera} title="Photo Requirements">
          <Col gap1>
            <span className="text-sm font-medium">Photo Instruction</span>
            <Col gap2>
              {photoInstructionsWithNewRow.map((instruction, index) => (
                <PhotoInstructionRow
                  key={instruction.id}
                  instruction={instruction}
                  index={index}
                  isLast={index === photoInstructionsWithNewRow.length - 1}
                  onUpdate={updatePhotoInstruction}
                  onRemove={removePhotoInstruction}
                  onAdd={addPhotoInstruction}
                />
              ))}
            </Col>
          </Col>
        </FormSection>
      </Col>

      <Row xEnd gap3 className="border-t border-neutral/15 p-5">
        <Button
          variant="outline"
          color="neutral"
          isDisabled={!hasUnsavedChanges}
          onClick={handleDiscard}
        >
          Discard
        </Button>
        <Button isDisabled={!hasUnsavedChanges} onClick={handleSave}>
          Save Changes
        </Button>
      </Row>
    </Col>
  );
};
